import { useState, useEffect, useCallback, useMemo } from "react";
import AdminLayout from "./AdminLayout";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Search, Filter, Loader2, LayoutGrid, List, X } from "lucide-react";

import { PipelineView } from "./quotes/PipelineView";
import { TableView } from "./quotes/TableView";
import { QuoteModal } from "./quotes/QuoteModal";

const formatStatus = (s) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const PIPELINE_STAGES = [
  { key: "new", label: "New Leads", color: "border-blue-400 bg-blue-50" },
  { key: "estimated", label: "Estimated", color: "border-yellow-400 bg-yellow-50" },
  { key: "survey_required", label: "Needs Survey", color: "border-orange-400 bg-orange-50" },
  { key: "quoted", label: "Quoted", color: "border-purple-400 bg-purple-50" },
  { key: "deposit_paid", label: "Deposit Paid", color: "border-green-400 bg-green-50" },
  { key: "confirmed", label: "Confirmed", color: "border-emerald-400 bg-emerald-50" },
  { key: "expired", label: "Expired", color: "border-slate-300 bg-slate-50" },
];

const TAG_OPTIONS = ["urgent", "high_risk", "VIP", "follow_up"];

const STATUSES = [
  { value: "new", label: "New" },
  { value: "estimated", label: "Estimated" },
  { value: "survey_required", label: "Survey Required" },
  { value: "quoted", label: "Quoted" },
  { value: "deposit_paid", label: "Deposit Paid" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  estimated: "bg-yellow-100 text-yellow-700",
  survey_required: "bg-orange-100 text-orange-700",
  quoted: "bg-purple-100 text-purple-700",
  deposit_paid: "bg-green-100 text-green-700",
  confirmed: "bg-green-100 text-green-700",
  completed: "bg-slate-100 text-slate-700",
  cancelled: "bg-red-100 text-red-700",
  expired: "bg-slate-200 text-slate-500",
};

const getStatusColor = (status) => STATUS_COLORS[status] || "bg-slate-100 text-slate-700";

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("table");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [postcodeFilter, setPostcodeFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [surveyFilter, setSurveyFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateData, setUpdateData] = useState({ status: "", final_price: "", notes: "" });

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (postcodeFilter) params.append("postcode", postcodeFilter);
      if (serviceFilter) params.append("service_type", serviceFilter);
      if (surveyFilter === "yes") params.append("survey_required", "true");
      if (surveyFilter === "no") params.append("survey_required", "false");
      if (tagFilter) params.append("tag", tagFilter);
      const response = await api.get(`/admin/quotes?${params.toString()}`);
      setQuotes(response.data.quotes);
      setTotal(response.data.total);
    } catch {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, postcodeFilter, serviceFilter, surveyFilter, tagFilter]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const handleUpdateQuote = async () => {
    if (!selectedQuote) return;
    setUpdating(true);
    try {
      await api.put(`/admin/quotes/${selectedQuote.quote_id}`, {
        status: updateData.status || selectedQuote.status,
        final_price: updateData.final_price ? parseFloat(updateData.final_price) : null,
        notes: updateData.notes || null,
      });
      toast.success("Quote updated");
      setEditMode(false);
      setSelectedQuote(null);
      fetchQuotes();
    } catch {
      toast.error("Failed to update quote");
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleTag = async (quoteId, currentTags, tag) => {
    const tags = currentTags || [];
    const newTags = tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag];
    try {
      await api.put(`/admin/quotes/${quoteId}/tags`, { tags: newTags });
      toast.success("Tags updated");
      fetchQuotes();
    } catch {
      toast.error("Failed to update tags");
    }
  };

  const handleReactivate = async (quoteId) => {
    try {
      await api.put(`/admin/quotes/${quoteId}/reactivate`);
      toast.success("Quote reactivated");
      setSelectedQuote(null);
      fetchQuotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reactivate");
    }
  };

  const handleSendFollowUp = async (quoteId) => {
    try {
      const res = await api.post(`/admin/quotes/${quoteId}/follow-up`);
      const status = res.data.email_status;
      toast.success(status === "sent" ? "Follow-up email sent" : "Follow-up queued (SendGrid not configured)");
    } catch {
      toast.error("Failed to send follow-up");
    }
  };

  const filteredQuotes = useMemo(
    () => quotes.filter((quote) => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      return (
        quote.from_postcode?.toLowerCase().includes(s) ||
        quote.to_postcode?.toLowerCase().includes(s) ||
        quote.customer?.name?.toLowerCase().includes(s) ||
        quote.customer?.email?.toLowerCase().includes(s)
      );
    }),
    [quotes, searchTerm]
  );

  const pipelineData = useMemo(() => {
    const grouped = {};
    PIPELINE_STAGES.forEach((s) => (grouped[s.key] = []));
    filteredQuotes.forEach((q) => { if (grouped[q.status]) grouped[q.status].push(q); });
    return grouped;
  }, [filteredQuotes]);

  const clearFilters = () => {
    setStatusFilter(""); setSearchTerm(""); setPostcodeFilter("");
    setServiceFilter(""); setSurveyFilter(""); setTagFilter("");
  };

  const hasActiveFilters = statusFilter || postcodeFilter || serviceFilter || surveyFilter || tagFilter;

  const openView = (q) => { setSelectedQuote(q); setEditMode(false); };
  const openEdit = (q) => {
    setSelectedQuote(q); setEditMode(true);
    setUpdateData({ status: q.status, final_price: q.final_price || "", notes: "" });
  };

  return (
    <AdminLayout title="Quotes">
      {/* Toolbar */}
      <div className="bg-white border border-slate-200 p-4 mb-4" data-testid="quotes-toolbar">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by postcode, name or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="quotes-search" />
          </div>
          <div className="flex gap-2 items-center">
            <div className="sm:w-40">
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="quotes-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant={showAdvanced ? "default" : "outline"} size="sm" onClick={() => setShowAdvanced(!showAdvanced)} data-testid="toggle-advanced-filters">
              <Filter className="w-4 h-4 mr-1" /> Filters
            </Button>
            <div className="flex border border-slate-200 rounded overflow-hidden">
              <button className={`px-3 py-2 text-sm ${viewMode === "table" ? "bg-[#0F172A] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`} onClick={() => setViewMode("table")} data-testid="view-table">
                <List className="w-4 h-4" />
              </button>
              <button className={`px-3 py-2 text-sm ${viewMode === "pipeline" ? "bg-[#0F172A] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`} onClick={() => setViewMode("pipeline")} data-testid="view-pipeline">
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {showAdvanced && (
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="advanced-filters">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Postcode Area</label>
              <Input placeholder="e.g. CF10" value={postcodeFilter} onChange={(e) => setPostcodeFilter(e.target.value)} data-testid="filter-postcode" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Service Type</label>
              <Select value={serviceFilter || "all"} onValueChange={(v) => setServiceFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-service"><SelectValue placeholder="Any Service" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Service</SelectItem>
                  <SelectItem value="packing">Packing</SelectItem>
                  <SelectItem value="storage">Storage</SelectItem>
                  <SelectItem value="dismantling">Dismantling</SelectItem>
                  <SelectItem value="specialist">Specialist Items</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Survey Required</label>
              <Select value={surveyFilter || "all"} onValueChange={(v) => setSurveyFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-survey"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Tag</label>
              <Select value={tagFilter || "all"} onValueChange={(v) => setTagFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-tag"><SelectValue placeholder="Any Tag" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Tag</SelectItem>
                  {TAG_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="col-span-2 sm:col-span-4 text-slate-500">
                <X className="w-4 h-4 mr-1" /> Clear all filters
              </Button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#FF5A1F]" /></div>
      ) : (
        viewMode === "pipeline"
          ? <PipelineView stages={PIPELINE_STAGES} data={pipelineData} getStatusColor={getStatusColor} onView={openView} onEdit={openEdit} onToggleTag={handleToggleTag} />
          : <TableView quotes={filteredQuotes} getStatusColor={getStatusColor} onView={openView} onEdit={openEdit} onToggleTag={handleToggleTag} />
      )}

      <div className="mt-4 text-sm text-slate-500" data-testid="quotes-count">Showing {filteredQuotes.length} of {total} quotes</div>

      <Dialog open={!!selectedQuote} onOpenChange={() => { setSelectedQuote(null); setEditMode(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editMode ? "Edit Quote" : "Quote Details"}</DialogTitle></DialogHeader>
          {selectedQuote && (
            <QuoteModal
              quote={selectedQuote} editMode={editMode} updating={updating}
              updateData={updateData} setUpdateData={setUpdateData}
              onSave={handleUpdateQuote} onReactivate={handleReactivate}
              onSendFollowUp={handleSendFollowUp} getStatusColor={getStatusColor}
              statuses={STATUSES}
            />
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminQuotes;
